'use client';

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ListColumnConfig,
  ListSegmentConfig,
  ListWatchConfig,
  UserListDetail,
  UserListSummary,
  UserListsResponse,
} from '@/lib/backend-api';
import { throwIfInsufficientCredits } from '@/hooks/use-alerts';
import { useSession } from '@/hooks/use-session';

const LISTS_KEY = ['lists'] as const;
const listDetailKey = (
  listId: number,
  sort?: string,
  direction?: 'asc' | 'desc',
  offset?: number,
  segmentIds?: string[],
) =>
  [
    'lists', 'detail', listId, sort ?? null, direction ?? null, offset ?? 0,
    // Sorted and joined so the same selection reached in a different click
    // order hits one cache entry.
    [...(segmentIds ?? [])].sort().join(',') || null,
  ] as const;

/** Rows per page of a list. Matches the backend's default page size. */
export const LIST_PAGE_SIZE = 100;

/**
 * Expected plan-gate outcomes, kept out of the rejected-promise path the same
 * way the watchlist hook does: hitting a cap is a product state, not an error.
 */
export type ListGate = 'pro' | 'limit';

export class ListGateError extends Error {
  constructor(
    readonly gate: ListGate,
    readonly limit?: number,
  ) {
    super(gate === 'pro' ? 'Lists requires Pro' : `List limit ${limit} reached`);
    this.name = 'ListGateError';
  }
}

async function parseGate(res: Response): Promise<ListGateError | null> {
  if (res.status !== 403) return null;
  const body = (await res.json().catch(() => null)) as {
    code?: string;
    limit?: number;
  } | null;
  if (body?.code === 'lists_pro_required') return new ListGateError('pro');
  if (body?.code === 'lists_limit' || body?.code === 'list_companies_limit') {
    return new ListGateError('limit', Number(body.limit) || undefined);
  }
  return null;
}

/** The tier's caps, returned alongside the list summaries by GET /api/lists. */
export interface ListsLimits {
  tier: 'free' | 'pro' | 'enterprise';
  lists: number;
  companiesPerList: number | null;
}

type ListsQueryData = {
  data: UserListsResponse;
  limits: ListsLimits | null;
};

async function fetchLists(): Promise<ListsQueryData> {
  const res = await fetch('/api/lists');
  if (!res.ok) throw new Error(`/api/lists returned ${res.status}`);
  const json = await res.json();
  return {
    data: json.data as UserListsResponse,
    limits: (json.limits ?? null) as ListsLimits | null,
  };
}

async function fetchListDetail(
  listId: number,
  sort?: string,
  direction?: 'asc' | 'desc',
  offset = 0,
  limit = LIST_PAGE_SIZE,
  segmentIds?: string[],
): Promise<UserListDetail> {
  const params = new URLSearchParams();
  if (sort) params.set('sort', sort);
  if (direction) params.set('direction', direction);
  params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  if (segmentIds?.length) params.set('segments', segmentIds.join(','));
  const res = await fetch(`/api/lists/${listId}?${params.toString()}`);
  if (!res.ok) throw new Error(`/api/lists/${listId} returned ${res.status}`);
  const json = await res.json();
  return json.data as UserListDetail;
}

/**
 * Every eu_id on the list, independent of the page being viewed.
 *
 * The detail read is one page, but "is this company already on the list"
 * is a question about the whole list — the add typeahead, the import
 * wizard's already-present count and the export all need it. Ids only, so
 * it stays cheap for a 10,000-company list.
 */
export function useListEuIds(listId: number) {
  const { data: session } = useSession();
  const { data } = useQuery<string[]>({
    queryKey: ['lists', 'eu-ids', listId],
    queryFn: async () => {
      const res = await fetch(`/api/lists/${listId}/eu-ids`);
      if (!res.ok) throw new Error(`eu-ids returned ${res.status}`);
      const json = await res.json();
      return (json.data?.eu_ids ?? []) as string[];
    },
    enabled: !!session && Number.isFinite(listId) && listId > 0,
    staleTime: 60 * 1000,
  });
  return useMemo(() => new Set(data ?? []), [data]);
}

/**
 * Every row of a list, paged through server-side. Used by the export, which
 * must write the whole list and not just the page on screen.
 */
export async function fetchAllListRows(
  listId: number,
  sort?: string,
  direction?: 'asc' | 'desc',
): Promise<UserListDetail['items']> {
  const PAGE = 500; // the backend's per-request ceiling
  const rows: UserListDetail['items'] = [];
  for (let offset = 0; ; offset += PAGE) {
    const page = await fetchListDetail(listId, sort, direction, offset, PAGE);
    rows.push(...page.items);
    if (rows.length >= page.company_count || page.items.length === 0) break;
  }
  return rows;
}

/**
 * List summaries + create/delete. Shared by the avatar menu ("My Lists"
 * submenu) and the overview page — one cache, one fetch.
 */
export function useLists() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<ListsQueryData>({
    queryKey: LISTS_KEY,
    queryFn: fetchLists,
    enabled: !!session,
    staleTime: 5 * 60 * 1000,
  });

  const lists = useMemo(() => data?.data.items ?? [], [data]);
  const limits = data?.limits ?? null;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: LISTS_KEY });

  const createMutation = useMutation({
    mutationFn: async (name: string): Promise<UserListSummary> => {
      const res = await fetch('/api/lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const gate = await parseGate(res);
      if (gate) throw gate;
      if (!res.ok) throw new Error(`POST /api/lists returned ${res.status}`);
      const json = await res.json();
      return json.data as UserListSummary;
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (listId: number): Promise<void> => {
      const res = await fetch(`/api/lists/${listId}`, { method: 'DELETE' });
      if (!res.ok)
        throw new Error(`DELETE /api/lists/${listId} returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  /** Returns the created list, or the gate that blocked it. */
  const create = async (
    name: string,
  ): Promise<UserListSummary | ListGateError> => {
    try {
      return await createMutation.mutateAsync(name);
    } catch (error) {
      if (error instanceof ListGateError) return error;
      throw error;
    }
  };

  return {
    lists,
    limits,
    isLoading,
    isError,
    refetch,
    create,
    isCreating: createMutation.isPending,
    remove: (listId: number) => deleteMutation.mutateAsync(listId),
    isRemoving: deleteMutation.isPending,
  };
}

/**
 * One list with hydrated company rows. `sort`/`direction` re-fetch server-side
 * sorted rows (the backend sorts exactly like /search does); `segmentIds`
 * narrows the list to the given segments before the page is cut, so
 * `filtered_count` — not `company_count` — is what pagination counts.
 */
export function useListDetail(
  listId: number,
  sort?: string,
  direction?: 'asc' | 'desc',
  offset = 0,
  segmentIds?: string[],
) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<UserListDetail>({
    queryKey: listDetailKey(listId, sort, direction, offset, segmentIds),
    queryFn: () =>
      fetchListDetail(listId, sort, direction, offset, LIST_PAGE_SIZE, segmentIds),
    enabled: !!session && Number.isFinite(listId) && listId > 0,
    // Keeps the current page on screen while the next one loads, so paging
    // doesn't flash the whole table away.
    placeholderData: (previous) => previous,
    staleTime: 60 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lists'] });
  };

  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(`/api/lists/${listId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!res.ok)
        throw new Error(`PATCH /api/lists/${listId} returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const configMutation = useMutation({
    mutationFn: async (body: {
      column_config?: ListColumnConfig | null;
      segment_config?: ListSegmentConfig | null;
    }) => {
      const res = await fetch(`/api/lists/${listId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok)
        throw new Error(`PATCH /api/lists/${listId} returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const addMutation = useMutation({
    mutationFn: async (euId: string): Promise<void> => {
      const res = await fetch(
        `/api/lists/${listId}/companies/${encodeURIComponent(euId)}`,
        { method: 'POST' },
      );
      const gate = await parseGate(res);
      if (gate) throw gate;
      if (!res.ok) throw new Error(`list add returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: async (euId: string): Promise<void> => {
      const res = await fetch(
        `/api/lists/${listId}/companies/${encodeURIComponent(euId)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error(`list remove returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const bulkRemoveMutation = useMutation({
    mutationFn: async (euIds: string[]): Promise<number> => {
      const res = await fetch(`/api/lists/${listId}/companies/bulk-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eu_ids: euIds }),
      });
      if (!res.ok) throw new Error(`bulk remove returned ${res.status}`);
      const json = (await res.json()) as { data: { removed: number } };
      return json.data.removed;
    },
    onSuccess: invalidate,
  });

  /**
   * Copy the selection onto another list. "Move" is copy-then-remove rather
   * than a dedicated endpoint: the target may be full, and a move that
   * removed first would drop companies on the floor when the add is refused.
   */
  const copyToList = async (
    targetListId: number,
    euIds: string[],
    { move = false }: { move?: boolean } = {},
  ): Promise<{ added: number } | ListGateError> => {
    const res = await fetch(`/api/lists/${targetListId}/companies/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eu_ids: euIds }),
    });
    const gate = await parseGate(res);
    if (gate) return gate;
    if (!res.ok) throw new Error(`bulk add returned ${res.status}`);
    const json = (await res.json()) as { data: { added: number } };
    if (move) await bulkRemoveMutation.mutateAsync(euIds);
    invalidate();
    return { added: json.data.added };
  };

  const watchMutation = useMutation({
    mutationFn: async (config: ListWatchConfig): Promise<void> => {
      const res = await fetch(`/api/lists/${listId}/watch`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      await throwIfInsufficientCredits(res);
      if (!res.ok) throw new Error(`list watch PUT returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const deleteWatchMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await fetch(`/api/lists/${listId}/watch`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`list watch DELETE returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  const shareMutation = useMutation({
    mutationFn: async (): Promise<string> => {
      const res = await fetch(`/api/lists/${listId}/share`, { method: 'POST' });
      if (!res.ok) throw new Error(`list share returned ${res.status}`);
      const json = await res.json();
      return (json.data as { share_token: string }).share_token;
    },
    onSuccess: invalidate,
  });

  const revokeShareMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await fetch(`/api/lists/${listId}/share`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`list unshare returned ${res.status}`);
    },
    onSuccess: invalidate,
  });

  /**
   * Returns `'added'`, or the gate that blocked the add — the gate carries
   * the tier's `limit` so the dialog can show the number the server
   * actually enforced.
   */
  const addCompany = async (euId: string): Promise<'added' | ListGateError> => {
    try {
      await addMutation.mutateAsync(euId);
      return 'added';
    } catch (error) {
      if (error instanceof ListGateError) return error;
      throw error;
    }
  };

  return {
    list: data ?? null,
    isLoading,
    isError,
    refetch,
    rename: (name: string) => renameMutation.mutateAsync(name),
    updateConfig: (body: {
      column_config?: ListColumnConfig | null;
      segment_config?: ListSegmentConfig | null;
    }) => configMutation.mutateAsync(body),
    addCompany,
    isAdding: addMutation.isPending,
    removeCompany: (euId: string) => removeMutation.mutateAsync(euId),
    isRemoving: removeMutation.isPending,
    bulkRemoveCompanies: (euIds: string[]) =>
      bulkRemoveMutation.mutateAsync(euIds),
    copyToList,
    isBulkBusy: bulkRemoveMutation.isPending,
    share: () => shareMutation.mutateAsync(),
    revokeShare: () => revokeShareMutation.mutateAsync(),
    isSharing: shareMutation.isPending || revokeShareMutation.isPending,
    saveWatch: (config: ListWatchConfig) => watchMutation.mutateAsync(config),
    deleteWatch: () => deleteWatchMutation.mutateAsync(),
    isSavingWatch: watchMutation.isPending || deleteWatchMutation.isPending,
  };
}
